import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { flushSync } from 'react-dom';
import {
  AWAY_IN_MS,
  AWAY_OUT_MS,
  CONTRACT_MS,
  FLIGHT_IN_MS,
  HALL_X,
  HAND_X,
  RING_IN_MS,
  SETTLE_K,
  SETTLE_MS,
  SPREAD_MS,
  SWELL,
  SWELL_MS,
  contractFrames,
  frameOf,
  inFrames,
  spreadFrames,
} from '@/components/brand/coreFlight';
import { CORE_R, MARK_HALF } from '@/components/brand/geometry';
import type { MarkCue } from '@/components/brand/KanameMark';
import {
  useObserverOptional,
  type CorePort,
  type HomeCue,
  type MenuLanes,
} from '@/components/observer/observerContext';
import { HOME_GESTURE_MS } from '@/components/observer/observerEvents';
import { prefersReducedMotion } from './entrance';
import type { NavTone } from './types';
import type { SlidingBox } from './useSlidingBox';

/*
  Дом ядра в главном меню — для помощника (spec-3, spec-3-motion §7). Работает только внутри
  ObserverProvider home="core" («Основа»); без него меню ровно прежнее, знак новых функций не получает
  (решение Максима 21.10) — отклики дома здесь принадлежат помощнику.

  Меню отдаёт провайдеру CorePort: где ядро, план своего дома (коридор, порог, пункты), отклик дома и
  передачу ядра курьеру (этап D2). Ядро — сфера в знаке на главной или выделение пункта в разделе.
  - Покой — метка data-core-home: нет перелёта ядра, знакомства и загрузки, ядро не в отлучке.
  - Отдать: знак — ядро набухает (×1,35) и чуть сдвигается против выхода, затем в знаке кольцо
    (проявляется за 160 мс); выделение — таблетка стягивается в каплю у левой кромки, левее значка (HAND_X),
    с 60 % на пункте проявляется контур (data-core-away). Кадр передачи — в одном кадре со скрытием дома
    (flushSync). Пока дом отдаёт ядро (releasing), меню не начинает своего перелёта.
  - Принять: в знак — ядро садится с ×1,3; в выделение — капля растекается в таблетку, контур гаснет с
    70 %. Принимает тот дом, что сейчас: человек мог перейти по меню, пока ядро было в отлучке.
  - Отклик: на ядре в знаке (KanameMark cue) или на выделении ([data-gesture] из motion.css).
  - Ритм ожидания на выделении — свечение (pill-await) по общим часам со знаком (startTime 0).
*/

type CoreAt = 'home' | 'pill';
/** Откуда ушло ядро: из знака или из выделения. */
export type CoreAway = 'mark' | 'pill' | null;

interface CoreHomeOptions {
  asideRef: RefObject<HTMLElement | null>;
  pillRef: RefObject<HTMLSpanElement | null>;
  coreAt: CoreAt;
  flight: CoreAt | null;
  box: SlidingBox;
  /** Знакомство или загрузка: ядро ещё не дома. */
  settling: boolean;
  /** Меню спрятано («Фокус»): дома не видно. */
  hidden: boolean;
  homeActive: boolean;
  /** Тон дома (окраска по состоянию): капля уходит и возвращается в нём. */
  tone: NavTone;
  /** Меняется, когда меняется дом: переход по меню, раскрытие и свёртывание, «Фокус». */
  homeKey: string;
}

/** Коридор кончается над строкой «я»: столько px до неё (spec-3-motion §1). */
const HALL_FOOT = 8;

/** Радиус ядра на экране — по знаку в меню. */
function coreRadius(aside: HTMLElement): number {
  const svg = aside.querySelector('[data-brand-home] svg');
  const w = svg ? svg.getBoundingClientRect().width : 40;
  return (CORE_R / (MARK_HALF * 2)) * w;
}

/** Контур пункта (data-core-away): темп — из coreFlight; «Замедлить ×4» замедляет и его. */
function contourOn(el: HTMLElement, mode: '' | 'out', ms: number) {
  el.style.setProperty('--core-away-ms', `${ms}ms`);
  el.setAttribute('data-core-away', mode);
}

function contourGone(el: HTMLElement) {
  el.removeAttribute('data-core-away');
  el.style.removeProperty('--core-away-ms');
}

/** Группа ядра в знаке (сфера с бликом), если ядро дома. */
function markCore(aside: HTMLElement): SVGGElement | null {
  return aside.querySelector<SVGGElement>('[data-brand-home] [data-slot="observer"]');
}

export function useCoreHome(o: CoreHomeOptions) {
  const observer = useObserverOptional();
  const core = observer?.home === 'core' ? observer : null;
  const [away, setAway] = useState<CoreAway>(null);
  // Дом отдаёт ядро (знак набухает, таблетка стягивается): ядро ещё дома, но уже не в покое — меню своего
  // перелёта не начинает (R41: одно живое ядро), дом сменился — капля пойдёт к новому от прежнего.
  const [releasing, setReleasing] = useState<CoreAway>(null);
  const [markCue, setMarkCue] = useState<MarkCue | null>(null);
  const rest =
    !!core &&
    !o.settling &&
    o.flight === null &&
    away === null &&
    releasing === null &&
    (o.coreAt === 'home' || o.box.visible);
  const slow = !!core?.settings.slowMo;

  // Порт живёт дольше рендера: всё нужное читает из ссылки на последние значения.
  const live = useRef({ ...o, away, releasing, rest, slow });
  useLayoutEffect(() => {
    live.current = { ...o, away, releasing, rest, slow };
  });
  const listeners = useRef(new Set<() => void>());
  const timers = useRef<number[]>([]);

  const port = useMemo<CorePort>(() => {
    /** «Замедлить ×4» на стенде: всё во столько раз дольше. */
    const k = () => (live.current.slow ? 4 : 1);
    const later = (fn: () => void, ms: number) => {
      timers.current.push(window.setTimeout(fn, ms));
    };

    const origin: CorePort['origin'] = () => {
      const { asideRef, coreAt, box, rest: atRest, hidden, tone } = live.current;
      const aside = asideRef.current;
      const svg = aside?.querySelector('[data-brand-home] svg');
      if (!aside || !svg) return null;
      const m = svg.getBoundingClientRect();
      const r = (CORE_R / (MARK_HALF * 2)) * m.width;
      if (coreAt === 'pill' && box.visible) {
        const a = aside.getBoundingClientRect();
        return {
          // Дом-выделение для курьера — точка передачи у левой кромки таблетки, левее значка.
          x: a.left + box.left + HAND_X,
          y: a.top + box.top + box.height / 2,
          r,
          kind: 'pill',
          rest: atRest,
          shown: !hidden,
          tone,
        };
      }
      return {
        x: m.left + m.width / 2,
        y: m.top + m.height / 2,
        r,
        kind: 'mark',
        rest: atRest,
        shown: !hidden,
        tone,
      };
    };

    const lanes = (): MenuLanes => {
      const { asideRef, homeActive } = live.current;
      const aside = asideRef.current!;
      const a = aside.getBoundingClientRect();
      const nav = aside.querySelector('nav');
      const items = Array.from(aside.querySelectorAll<HTMLElement>('[data-menu-item]')).map((el) => ({
        id: `nav.${el.dataset.menuItem}`,
        rect: el.getBoundingClientRect(),
      }));
      const brand = aside.querySelector('[data-brand-home]')?.closest('button')?.getBoundingClientRect();
      const brandBottom = brand ? brand.bottom : a.top;
      const firstTop = items.length > 0 ? items[0].rect.top : brandBottom;
      // Низ коридора — над строкой «я» (за прокруткой пунктов); без неё — над низом меню.
      const foot = nav?.nextElementSibling?.getBoundingClientRect().top ?? a.bottom - HALL_FOOT;
      return {
        menu: a,
        scroll: nav ? nav.getBoundingClientRect() : a,
        hallX: a.left + HALL_X,
        sillY: (brandBottom + firstTop) / 2,
        hallBottom: foot - HALL_FOOT,
        items,
        sillGate: homeActive,
      };
    };

    const release: CorePort['release'] = (dir) =>
      new Promise<void>((done) => {
        const { asideRef, pillRef, coreAt, box, away: was, releasing: busy } = live.current;
        const aside = asideRef.current;
        const pill = pillRef.current;
        if (!aside || !pill || was || busy) return done();
        if (coreAt === 'pill' && box.visible) {
          const target = aside.querySelector<HTMLElement>('[data-pill-target="true"]');
          setReleasing('pill');
          pill.dataset.flight = '';
          // Видимость таблетки ведёт React (на время отдачи — 0); кадры стягивания несут свою.
          const anim = pill.animate(contractFrames(box, coreRadius(aside)), {
            duration: CONTRACT_MS * k(),
            fill: 'forwards',
          });
          later(() => target && contourOn(target, '', AWAY_IN_MS * k()), CONTRACT_MS * 0.6 * k());
          anim.onfinish = () => {
            // Кадр передачи: выделение гаснет сразу, капля курьера встаёт на его место в этом же кадре.
            pill.dataset.away = '';
            flushSync(() => {
              setReleasing(null);
              setAway('pill');
            });
            anim.cancel();
            // Переезды выключены отлучкой (data-away), метка полёта не нужна.
            delete pill.dataset.flight;
            done();
          };
          return;
        }
        const g = markCore(aside);
        if (!g || prefersReducedMotion()) {
          flushSync(() => setAway('mark'));
          return done();
        }
        // Подготовка: ядро набухает и на 1 px подаётся против выхода (в единицах знака).
        setReleasing('mark');
        const unit =
          (MARK_HALF * 2) /
          Math.max(1, aside.querySelector('[data-brand-home] svg')!.getBoundingClientRect().width);
        const [dx, dy] = dir === 'up' ? [0, unit] : dir === 'down' ? [0, -unit] : [unit, 0];
        const anim = g.animate(
          [
            { scale: 1, translate: '0 0' },
            { scale: SWELL, translate: `${dx}px ${dy}px` },
          ],
          { duration: SWELL_MS * k(), easing: 'ease-out', fill: 'forwards' },
        );
        anim.onfinish = () => {
          flushSync(() => {
            setReleasing(null);
            setAway('mark');
          });
          done();
        };
      });

    const receive: CorePort['receive'] = () =>
      new Promise<void>((done) => {
        const { asideRef, pillRef, coreAt, box, away: was } = live.current;
        const aside = asideRef.current;
        const pill = pillRef.current;
        if (!aside || !pill || !was) return done();
        const contourOff = () => {
          for (const el of Array.from(aside.querySelectorAll<HTMLElement>('[data-core-away]'))) {
            contourOn(el, 'out', AWAY_OUT_MS * k());
            later(() => contourGone(el), AWAY_OUT_MS * k());
          }
        };
        delete pill.dataset.away;
        if (coreAt === 'pill' && box.visible) {
          pill.dataset.flight = '';
          // Кадры растекания несут видимость сами; после них её снова ведёт React — человек мог уйти на
          // главную, пока капля растекалась, и таблетка там не нужна.
          const anim = pill.animate(spreadFrames(box, coreRadius(aside)), {
            duration: SPREAD_MS * k(),
            fill: 'forwards',
          });
          later(contourOff, SPREAD_MS * 0.7 * k());
          const back = () => {
            flushSync(() => setAway(null));
            requestAnimationFrame(() => delete pill.dataset.flight);
            done();
          };
          anim.onfinish = () => {
            // Человек ушёл на главную, пока капля растекалась: ядро не прыгает в знак, а летит туда перелётом
            // R41 с места, где растеклось (отлучка держится до конца полёта — меню своего не начинает).
            const mark = aside.querySelector('[data-brand-home] svg');
            if (live.current.coreAt === 'home' && mark && !prefersReducedMotion()) {
              const a = aside.getBoundingClientRect();
              const m = mark.getBoundingClientRect();
              const core = {
                x: m.left - a.left + m.width / 2,
                y: m.top - a.top + m.height / 2,
                r: coreRadius(aside),
              };
              const fly = pill.animate(inFrames(frameOf(pill), core, box, true), {
                duration: FLIGHT_IN_MS * k(),
                fill: 'forwards',
              });
              anim.cancel();
              fly.onfinish = () => {
                back();
                fly.cancel();
              };
              return;
            }
            back();
            anim.cancel();
          };
          return;
        }
        contourOff();
        flushSync(() => setAway(null));
        const g = markCore(aside);
        if (!g || prefersReducedMotion()) return done();
        const anim = g.animate([{ scale: SETTLE_K }, { scale: 1 }], {
          duration: SETTLE_MS * k(),
          easing: 'ease-out',
        });
        anim.onfinish = () => done();
      });

    const cue: CorePort['cue'] = (c: HomeCue) => {
      const { asideRef, pillRef, coreAt, flight, box } = live.current;
      if (prefersReducedMotion()) return;
      if (coreAt === 'home' || flight !== null || !box.visible) {
        setMarkCue({ gesture: c.gesture, at: c.at });
        return;
      }
      // Выделение: жест заново, даже тот же подряд — атрибут снимается и ставится после пересчёта стилей.
      const pill = pillRef.current;
      if (!pill || !asideRef.current) return;
      const g = c.gesture === 'burst' ? 'pulse' : c.gesture;
      pill.removeAttribute('data-gesture');
      void getComputedStyle(pill).getPropertyValue('animation-name');
      pill.setAttribute('data-gesture', g);
      later(() => {
        if (pill.getAttribute('data-gesture') === g) pill.removeAttribute('data-gesture');
      }, HOME_GESTURE_MS[g]);
    };

    const onChange: CorePort['onChange'] = (cb) => {
      listeners.current.add(cb);
      return () => listeners.current.delete(cb);
    };

    return { origin, lanes, release, receive, cue, onChange };
  }, []);

  const setCorePort = core?.setCorePort;
  useEffect(() => {
    if (!setCorePort) return;
    setCorePort(port);
    return () => setCorePort(null);
  }, [setCorePort, port]);

  // Дом сменился — сказать курьеру (пересборка пути, этап D2).
  useEffect(() => {
    for (const cb of listeners.current) cb();
  }, [o.homeKey]);

  // Ядро ушло из знака: кольцо проявляется с кадра передачи — ядро не исчезает, пока не видна капля.
  useLayoutEffect(() => {
    if (away !== 'mark' || prefersReducedMotion()) return;
    const ring = o.asideRef.current?.querySelector('[data-core-ring]');
    ring?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: RING_IN_MS * (slow ? 4 : 1) });
  }, [away, o.asideRef, slow]);

  // Человек перешёл по меню, пока ядро в отлучке из выделения: контур — на новом активном пункте.
  useLayoutEffect(() => {
    const aside = o.asideRef.current;
    if (away !== 'pill' || !aside) return;
    const target = aside.querySelector<HTMLElement>('[data-pill-target="true"]');
    for (const el of Array.from(aside.querySelectorAll<HTMLElement>('[data-core-away]'))) {
      if (el !== target) contourGone(el);
    }
    if (target && !target.hasAttribute('data-core-away')) contourOn(target, '', AWAY_IN_MS * (slow ? 4 : 1));
  }, [away, o.homeKey, o.asideRef, slow]);

  useEffect(
    () => () => {
      timers.current.forEach((t) => window.clearTimeout(t));
    },
    [],
  );

  // Ритм ожидания — только когда ядро дома (после приёма), на выделении — свечение по общим часам.
  const awaiting = !!core?.awaiting && away === null;
  const pillAwait = awaiting && o.coreAt === 'pill' && o.flight === null && o.box.visible;
  useLayoutEffect(() => {
    const pill = o.pillRef.current;
    if (!pillAwait || !pill) return;
    for (const a of pill.getAnimations({ subtree: true })) {
      if ((a as CSSAnimation).animationName === 'pill-await') a.startTime = 0;
    }
  }, [pillAwait, o.pillRef]);

  return {
    /** Провайдер помощника с домом core, иначе null — меню ровно прежнее. */
    core,
    away,
    /** Дом отдаёт ядро прямо сейчас (набухание, стягивание). */
    releasing,
    /** Ядро не в покое дома: в отлучке или отдаётся — своего перелёта у меню нет. */
    out: away ?? releasing,
    /** Ядро в покое: метка data-core-home. */
    rest,
    /** Отклик ядра в знаке; undefined — без провайдера (знак без колец отклика). */
    markCue: core ? markCue : undefined,
    awaiting,
    pillAwait,
    slow,
  };
}
